import { FFH_HOSTNAME } from './constants';
import { serialiseJsonLd } from './page-utils/structured-data';

/**
 * Matches the site on either host form. The cms host is deliberately not
 * matched: its URLs are WordPress media, which lives there and nowhere else.
 */
const SITE_URL_PATTERN =
  /^https?:\/\/(?:www\.)?fantasyfootballhub\.co\.uk(\/[^?#]*)?([?#].*)?$/;

const ROOT_PATH = '/';

/**
 * Canonical form of a site URL: www host, no trailing slash except on the
 * root, query and fragment carried over. This is what rel=canonical and
 * og:url already emit, so every other URL signal on the page must agree with
 * it or Google picks its own canonical (FFH-2549).
 */
export const toCanonicalSiteUrl = (url: string): string => {
  const match = SITE_URL_PATTERN.exec(url);
  if (!match) return url;

  const [, path = ROOT_PATH, suffix = ''] = match;
  const canonicalPath =
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  return `${FFH_HOSTNAME}${canonicalPath}${suffix}`;
};

const normaliseValue = (value: unknown): unknown => {
  if (typeof value === 'string') return toCanonicalSiteUrl(value);
  if (Array.isArray(value)) return value.map(normaliseValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, normaliseValue(entry)])
    );
  }
  return value;
};

/**
 * Rewrites every site URL inside a Yoast JSON-LD graph onto the canonical
 * form and re-serialises it HTML-safe, since parsing undoes any escaping the
 * CMS applied and React does not escape script text. Yoast emits the WordPress permalink form (bare host, trailing slash)
 * for WebPage @id, mainEntityOfPage, breadcrumb items and author URLs, and
 * Google weighs the @id over our rel=canonical.
 */
export const normaliseSchemaGraphUrls = (json: string): string => {
  try {
    return serialiseJsonLd(normaliseValue(JSON.parse(json)));
  } catch {
    return json;
  }
};
